import {
    Autocomplete,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';

import FormField, { type FormFieldType } from 'components/common/formikForm/formField';

import useTranslate from 'hooks/useTranslate';
import type { ProgramQuestion } from 'types';

import { optionsForQuestion, type ProgramQuestionValues } from '../formState';

interface QuestionFieldProps {
    question: ProgramQuestion;
    active: boolean;
    onValueChange: (value: string | string[]) => void | Promise<unknown>;
}

const EARLIEST_BIRTH_DATE = new Date(1921, 0, 1);

function parseDateOnly(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
}

function dateLimits(question: ProgramQuestion): { minDate?: Date; maxDate?: Date } {
    if (question.type !== 'date') {
        return {};
    }
    const bounded = Boolean(question.min_date || question.max_date || question.after_today);
    if (!bounded) {
        return { minDate: EARLIEST_BIRTH_DATE, maxDate: new Date() };
    }
    let minDate = question.min_date ? parseDateOnly(question.min_date) : undefined;
    if (question.after_today) {
        const tomorrow = new Date();
        tomorrow.setHours(0, 0, 0, 0);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (!minDate || tomorrow > minDate) {
            minDate = tomorrow;
        }
    }
    return {
        minDate,
        maxDate: question.max_date ? parseDateOnly(question.max_date) : undefined,
    };
}

function isYesNo(question: ProgramQuestion): boolean {
    return (
        question.type === 'select' &&
        question.options?.length === 2 &&
        question.options.includes('Yes') &&
        question.options.includes('No')
    );
}

function fieldType(question: ProgramQuestion): FormFieldType {
    if (question.type === 'date') {
        return 'date';
    }
    if (question.type === 'text') {
        return 'string';
    }
    return isYesNo(question) ? 'radio' : 'autosuggest';
}

function linkLabel(url: string): string {
    if (url.includes('recognized-organizations')) return 'Recognized organizations';
    if (url.includes('language-tool')) return 'Calculator Tool';
    if (url.includes('/noc.asp')) return 'Find your NOC';
    if (url.includes('/funds.asp')) return 'Settlement funds';
    if (url.includes('bankofcanada')) return 'Currency converter';
    if (url.includes('/work/list.asp')) return 'Ineligible employers';
    if (url.includes('/offer.asp')) return 'Offers of employment';
    if (url.includes('/newcomers/pt/')) return 'Provinces and territories';
    return 'Learn more';
}

function HelpText({ help }: { help: string }) {
    const parts = help.split(/(https?:\/\/\S+)/);
    return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {parts.map((part, index) =>
                part.startsWith('http') ? (
                    <Link
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                    >
                        {linkLabel(part)}
                    </Link>
                ) : (
                    <span key={index}>{part}</span>
                )
            )}
        </Typography>
    );
}

function selectedChecks(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
}

const QuestionField = ({ question, active, onValueChange }: QuestionFieldProps) => {
    const { translate } = useTranslate();
    const { values } = useFormikContext<ProgramQuestionValues>();
    const [field, meta, helpers] = useField<string | string[]>(question.name);
    const hasError = active && Boolean(meta.error);
    const choiceOptions = optionsForQuestion(question, values);
    const type = fieldType(question);
    const limits = dateLimits(question);
    const locked = Boolean(question.read_only) && Boolean(question.value);
    const showsProfileValue =
        Boolean(question.prefilled) &&
        (Array.isArray(field.value)
            ? field.value.length > 0 &&
              JSON.stringify(field.value) === JSON.stringify(question.value)
            : field.value !== '' && field.value === question.value);

    return (
        <Box sx={{ opacity: active ? 1 : 0.72 }}>
            <Typography
                variant="subtitle2"
                color={hasError ? 'error' : 'text.primary'}
                sx={{ fontWeight: 600 }}
            >
                {question.label}
                {question.required && active ? ' *' : ''}
            </Typography>
            {question.help ? <HelpText help={question.help} /> : null}
            {showsProfileValue ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                >
                    {translate('programQuestionPrefilled')}
                </Typography>
            ) : null}
            {question.depends_on?.any_of && question.depends_on.any_of.length > 0 ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                >
                    Required only when the related answer is {question.depends_on.any_of.join(', ')}
                    .
                </Typography>
            ) : question.depends_on?.value ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                >
                    Required only when the related answer is {question.depends_on.value}.
                </Typography>
            ) : null}

            <Box sx={{ mt: 1 }}>
                {question.type === 'checkboxes' && choiceOptions.length > 10 ? (
                    <Autocomplete
                        multiple
                        size="small"
                        options={choiceOptions}
                        value={selectedChecks(field.value)}
                        disabled={!active || locked}
                        onChange={(_event, selected) => {
                            helpers.setValue(selected);
                            onValueChange(selected);
                        }}
                        onBlur={() => helpers.setTouched(true)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={question.label}
                                error={hasError}
                                helperText={hasError ? meta.error : undefined}
                            />
                        )}
                    />
                ) : question.type === 'checkboxes' ? (
                    <FormControl error={hasError} fullWidth>
                        <FormGroup>
                            {choiceOptions.map((option) => {
                                const selected = selectedChecks(field.value);
                                return (
                                    <FormControlLabel
                                        key={option}
                                        disabled={!active || locked}
                                        control={
                                            <Checkbox
                                                size="small"
                                                color={hasError ? 'error' : 'primary'}
                                                sx={hasError ? { color: 'error.main' } : undefined}
                                                slotProps={{ input: { 'aria-invalid': hasError } }}
                                                checked={selected.includes(option)}
                                                onChange={(event) => {
                                                    const next = event.target.checked
                                                        ? [...selected, option]
                                                        : selected.filter(
                                                              (item) => item !== option
                                                          );
                                                    helpers.setValue(next);
                                                    onValueChange(next);
                                                }}
                                            />
                                        }
                                        label={option}
                                    />
                                );
                            })}
                        </FormGroup>
                        {hasError ? <FormHelperText>{meta.error}</FormHelperText> : null}
                    </FormControl>
                ) : null}
                {question.type === 'checkboxes' ? null : (
                    <FormField
                        key={choiceOptions.join('\n')}
                        name={question.name}
                        label=""
                        translateLabel={false}
                        type={type}
                        size="small"
                        disabled={!active || locked}
                        error={hasError}
                        maxLength={question.max_length ?? undefined}
                        options={choiceOptions.map((option) => ({
                            label: option,
                            value: option,
                        }))}
                        placeholder={type === 'autosuggest' ? 'Select' : undefined}
                        minDate={limits.minDate}
                        maxDate={limits.maxDate}
                        onChange={(value) => onValueChange(value == null ? '' : String(value))}
                    />
                )}
            </Box>
        </Box>
    );
};

export default QuestionField;
